import type { Booking, BookingWearer, IssueBookingInput } from '../api/bookings';
import { depositSplit } from './deposit';
import { isReadyToIssue } from './tones';

/**
 * What can go out of the door, and what has to be collected when it does.
 *
 * <p>
 * <strong>Partial issue is the ordinary case, not the exception.</strong> One garment is always
 * still at the tailor on pickup day, and a counter that could only issue all-or-nothing would send
 * a groom home empty-handed because his brother's sleeve is not finished. So the unit of the
 * screen is the wearer — a person collects their whole outfit — and a wearer whose garments are
 * not all ready can still be served with the ones that are.
 * </p>
 */

/** Whether a wearer is fully selected, partly selected, or not at all. */
export type SelectionState = 'all' | 'some' | 'none';

/** Garments of this wearer that could physically leave the shop today. */
export function issuableGarments(wearer: BookingWearer): BookingWearer['garments'] {
  return wearer.garments.filter((garment) => isReadyToIssue(garment.state));
}

/** Whether this wearer can be served at all — a wearer with nothing ready is shown but disabled. */
export function canIssue(wearer: BookingWearer): boolean {
  return issuableGarments(wearer).length > 0;
}

/**
 * How a wearer's checkbox should read, given what is ticked.
 *
 * Measured against what is *issuable* rather than against everything they are wearing: a wearer
 * with two ready garments and one at the tailor reads as fully selected when both ready ones are
 * ticked, because that is everything the counter can hand over.
 */
export function wearerSelection(
  wearer: BookingWearer,
  selected: ReadonlySet<number>,
): SelectionState {
  const issuable = issuableGarments(wearer);

  if (issuable.length === 0) {
    return 'none';
  }

  const ticked = issuable.filter((garment) => selected.has(garment.id)).length;

  if (ticked === 0) {
    return 'none';
  }

  return ticked === issuable.length ? 'all' : 'some';
}

/** Ticking a wearer takes everything of theirs that can go; unticking clears the lot. */
export function toggleWearer(wearer: BookingWearer, selected: ReadonlySet<number>): Set<number> {
  const next = new Set(selected);
  const issuable = issuableGarments(wearer);
  const clearing = wearerSelection(wearer, selected) === 'all';

  for (const garment of issuable) {
    if (clearing) {
      next.delete(garment.id);
    } else {
      next.add(garment.id);
    }
  }

  return next;
}

/** Everything issuable on the booking, which is what the screen starts with ticked. */
export function defaultSelection(booking: Booking): Set<number> {
  return new Set(
    booking.wearers.flatMap((wearer) => issuableGarments(wearer).map((garment) => garment.id)),
  );
}

export interface CollectionTotals {
  /** Rental still owed before this hand-over — the booking's own pre-tax figure. */
  rental: number;
  /** GST the hand-over's invoice adds, as far as it is not already covered by money taken. */
  tax: number;
  /**
   * Rental to take now, GST included. Excludes the deposit — money held is not money earned.
   *
   * <p>
   * <strong>The GST is in it because the hand-over raises the invoice.</strong> The drive of
   * 2026-10-05 collected everything a counter showed, handed over, and left the customer owing 780:
   * the balance is pre-tax until a document exists. The figure comes from the server's preview,
   * never from a rate the client applies.
   * </p>
   */
  balance: number;
  /** Deposit payable now, which is nothing if this shop took it at booking. */
  deposit: number;
  /** What the customer actually hands over. */
  total: number;
}

/**
 * What to collect at the hand-over. `taxToCome` is the preview's `addsToBalance` for the garments
 * ticked; money already taken beyond the pre-tax balance counts against it first.
 */
export function collectionTotals(booking: Booking, taxToCome = 0): CollectionTotals {
  const rental = Math.max(0, booking.balanceDue);
  const balance = Math.max(0, booking.balanceDue + taxToCome);
  const { due } = depositSplit(booking.deposit);

  return { rental, tax: balance - rental, balance, deposit: due, total: balance + due };
}

/**
 * The preview request for a selection, or null when nothing is ticked — **an empty list means
 * "everything ready" to the server**, so asking with one would price garments the clerk unticked.
 */
export function issuePreviewInput(selected: ReadonlySet<number>): IssueBookingInput | null {
  return selected.size === 0 ? null : { garmentIds: [...selected].sort((x, y) => x - y) };
}

/**
 * How many garments going out the counter must photograph or wave through — zero unless the shop
 * asks for a photograph at hand-over (`BookingDefaults.issueRequiresPhoto`), the server's rule.
 *
 * <p>
 * Where it is asked for, a garment going out without one is worth stopping at — but as a warning the
 * counter can override, not a hard block: a queue on a Saturday is a real thing and a shop that
 * cannot issue is a shop that loses the customer. <strong>Where it is not, it is never mentioned</strong>
 * (owner, 2026-10-17): some shops never photograph, and a box ticked on every hand-over protects
 * nothing.
 * </p>
 */
export function missingIssuePhotos(
  booking: Booking,
  selected: ReadonlySet<number>,
  required: boolean,
): number {
  if (!required) {
    return 0;
  }

  return booking.wearers
    .flatMap((wearer) => wearer.garments)
    .filter((garment) => selected.has(garment.id) && garment.photoCountOut === 0).length;
}

/**
 * Every accessory on the booking, ticked.
 *
 * <p>
 * <strong>Default-on, which is the counter's reality rather than a shortcut.</strong> The brooch
 * goes out with the sherwani; a clerk made to tick the obvious stops ticking and the shop loses the
 * record that lets it charge for a kalgi that never came back. Unticking is the exception — the
 * brooch lost last month, the spare safa the customer did not want — and it is one click.
 * </p>
 */
export function allAccessories(booking: Booking): Set<number> {
  return new Set(
    booking.wearers
      .flatMap((wearer) => wearer.garments)
      .flatMap((garment) => garment.accessories)
      .map((accessory) => accessory.id),
  );
}

/**
 * The accessory ids to report as having left, given what is going out and what is still ticked.
 *
 * <p>
 * Narrowed to the selected garments deliberately: the server refuses an accessory that is not on a
 * garment being issued, and a clerk issuing half the party should not have the other half's brooches
 * recorded as out of the shop.
 * </p>
 */
export function accessoriesLeaving(
  booking: Booking,
  selected: ReadonlySet<number>,
  ticked: ReadonlySet<number>,
): number[] {
  return booking.wearers
    .flatMap((wearer) => wearer.garments)
    .filter((garment) => selected.has(garment.id))
    .flatMap((garment) => garment.accessories)
    .filter((accessory) => ticked.has(accessory.id))
    .map((accessory) => accessory.id);
}
